import { useEffect, useMemo, useState } from 'react'
import { ethers } from 'ethers'
import { Contract } from '@ethersproject/contracts'
import { License } from '@mprolab/sdk'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import {
  MARKETPLACE_ABI,
  MULTICALL3_ABI,
  MULTICALL3_ADDRESS
} from 'utils/marketplace/abi'
import { useMarketplaceContext } from 'utils/marketplace/useMarketplaceContext'

/** tokenId → the id of the listing it is currently on. */
export type ActiveListings = Record<string, string>

/**
 * Which of these licences are on the marketplace right now.
 *
 * A holder with ten licences and three of them listed has, until now, had to
 * match token ids by eye to work out which is which. This is what lets the
 * table simply say so.
 *
 * Asked of the CONTRACT, not of our database: a listing exists on chain, and
 * the same question decides whether a payout may mint from the licence. One
 * Multicall3 round-trip covers the whole table — asking per licence would put
 * a request per row on every visit to the page.
 */
export const useActiveListingsForLicences = (
  licenses: License[] | undefined
): ActiveListings => {
  const ctx = useMarketplaceContext()
  const [listings, setListings] = useState<ActiveListings>({})

  // Only licences that carry an NFT can be listed, and the key keeps the
  // effect from re-running on every render of the same table.
  const tokens = useMemo(
    () =>
      (licenses ?? [])
        .filter((l) => l.tokenId && l.nftContract)
        .map((l) => ({ tokenId: String(l.tokenId), nft: String(l.nftContract) })),
    [licenses]
  )
  const key = tokens.map((t) => `${t.nft}:${t.tokenId}`).join(',')

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!ctx.marketplaceAddress || tokens.length === 0) {
        setListings({})
        return
      }
      try {
        const provider = getSimpleRpcProviderByChainId(
          ctx.chainId
        ) as ethers.providers.Provider
        const market = new Contract(
          ctx.marketplaceAddress,
          MARKETPLACE_ABI as unknown as ethers.ContractInterface,
          provider
        )
        const multicall = new Contract(
          MULTICALL3_ADDRESS,
          MULTICALL3_ABI as unknown as ethers.ContractInterface,
          provider
        )
        const calls = tokens.map((t) => ({
          target: ctx.marketplaceAddress,
          allowFailure: true,
          callData: market.interface.encodeFunctionData('activeListingIdOf', [
            t.nft,
            t.tokenId
          ])
        }))
        const raw: Array<{ success: boolean; returnData: string }> =
          await multicall.callStatic.aggregate3(calls)

        const next: ActiveListings = {}
        raw.forEach((result, i) => {
          if (!result.success) return
          try {
            const [id] = market.interface.decodeFunctionResult(
              'activeListingIdOf',
              result.returnData
            )
            // Zero means "not listed"; the contract uses it as the empty id.
            if (!id.isZero()) next[tokens[i].tokenId] = id.toString()
          } catch {
            // One unreadable answer must not cost the whole table its labels.
          }
        })
        if (!cancelled) setListings(next)
      } catch {
        // An unreadable marketplace is not proof that nothing is listed, so
        // leave the previous answer rather than claiming everything is free.
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [key, ctx.marketplaceAddress, ctx.chainId])

  return listings
}
